import { backendApi } from "@/services/backend/api";
import { Offer } from "@/services/backend/htz/offers/types/offer";
import { Filter } from "@/shared/lib/filter/filter";
import {
  providesEntity,
  providesList,
} from "@/services/backend/_lib/tag-helpers";
import {
  downloadResponseHandler,
  previewResponseHandler,
} from "@/services/backend/_lib/pdf-helpers";
import {
  DocumentBaseFilters,
  DocumentCommonFilters,
} from "@/services/backend/documents/types/filters";
import { OrdrBy } from "@/shared/lib/ordr/ordrBy";
import { Lst } from "@/shared/lib/lst/lst";
import { Reason } from "@bitory-gmbh/nidavellir";

const offerApi = backendApi.injectEndpoints({
  endpoints: (build) => ({
    htzOfferPut: build.mutation<Offer, PutRequest>({
      query: (request) => ({
        url: `/htz/offer`,
        method: "PUT",
        body: request,
      }),
      invalidatesTags: (_result, _e, request) => [
        { type: "HtzOffer", id: request.id },
        "HtzOffer",
      ],
    }),
    htzOfferShow: build.query<Offer, ShowRequest>({
      query: (request) => ({
        url: `/htz/offer`,
        method: "GET",
        params: request,
      }),
      providesTags: providesEntity("HtzOffer"),
    }),
    htzOfferList: build.query<Lst<Offer>, ListRequest>({
      query: (request) => ({
        url: `/htz/offer/list`,
        method: "GET",
        params: {
          request: btoa(JSON.stringify(request)),
        },
      }),
      providesTags: (result) => providesList(result?.items, "HtzOffer"),
    }),
    htzOfferSetImmutable: build.mutation<Offer, SetImmutableRequest>({
      query: (request) => ({
        url: `/htz/offer/set-immutable`,
        method: "PATCH",
        body: request,
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "HtzOffer", id: request.id },
        "HtzOffer",
        "NumbersDocumentNumber",
      ],
    }),
    htzOfferDelete: build.mutation<null, DeleteRequest>({
      query: (request) => ({
        url: `/htz/offer`,
        method: "DELETE",
        body: request,
      }),
      invalidatesTags: ["HtzOffer"],
    }),
    htzOfferPdfPreview: build.query<{ objectURL: string }, PreviewPDFRequest>({
      query: (request) => ({
        url: `/htz/offer`,
        method: "GET",
        params: request,
        headers: { Accept: "application/pdf" },
        responseHandler: previewResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      providesTags: (_r, _e, request) => [
        { type: "HtzOffer", id: request.offerId },
      ],
    }),
    htzOfferPdfDownload: build.mutation<
      { filename: string; objectURL: string },
      PDFRequest
    >({
      query: (request) => ({
        url: `/htz/offer`,
        method: "GET",
        params: { ...request, download: true },
        headers: { Accept: "application/pdf" },
        responseHandler: downloadResponseHandler,
        // IMPORTANT. Since this query will download the PDF under no circumstances
        // should the results be cached. This could end up using the users memory.
        cache: "no-cache",
      }),
      invalidatesTags: (_r, _e, request) => [
        { type: "HtzOffer", id: request.offerId },
      ],
    }),
  }),
});

export const {
  useHtzOfferPutMutation,
  useHtzOfferShowQuery,
  useHtzOfferListQuery,
  useLazyHtzOfferListQuery,
  useHtzOfferSetImmutableMutation,
  useHtzOfferDeleteMutation,
  useHtzOfferPdfPreviewQuery,
  useHtzOfferPdfDownloadMutation,
} = offerApi;

export type PutRequest = Offer;

export interface ShowRequest {
  id: string;
}

export interface ListRequest
  extends DocumentBaseFilters, DocumentCommonFilters {
  orderBy?: OrdrBy<string>[];
  date?: Filter<string>;
  immutable?: Filter<boolean>;
  limit?: number;
  offset?: number;
}

export interface List {
  items: Offer[];
  count: number;
}

export interface SetImmutableRequest {
  id: string;
  reason: Reason;
  setBy: string;
}

export enum OfferImmutabilityReason {
  SetByUser = "set_by_user",
}

export interface DeleteRequest {
  id: string;
}

export enum OfferDocument {
  ALL = "all",
  OFFER = "offer",
  ORDER = "order",
}

export interface PreviewPDFRequest {
  offerId: string;
  document: OfferDocument;
}

export interface PDFRequest {
  offerId: string;
  document: OfferDocument;
  download: boolean;
}
